import { PageLoading, PortalLoading, WorkspaceLoading } from "./components/LoadingScreen";
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { Suspense, lazy } from "react";
import { validateInboxSearch } from "./lib/inbox-search";
import { LoggedOutPage, LoginPage } from "./routes/login";
import { meQuery, ticketListQuery, ticketQuery, ticketLinksQuery, watchersQuery, viewsQuery, workflowStatusSetsQuery } from "./lib/queries";
import { ticketCountsQuery } from "./lib/staff-api";
import { toApiQuery } from "./lib/search-syntax";
import { composeContextQuery } from "./lib/queries-uxc2";
import { validateMailSearch } from "./lib/queries-mail"; // [M4-09]
// [TM-03] งานภายใน — search whitelist อยู่ที่ lib (กันดึงชั้นหลักของบอร์ดมาก่อนเวลา)
import { validateTasksSearch, type TasksSearch } from "./lib/tasks-api";
// [T-068] หน้ารายงาน — layout + แท็บย่อยเป็น route ของตัวเอง (code-split ชุดเดียว)
import { normalizeReportsSearch, type ReportsSearch } from "./lib/queries-reports";
const ReportsLayoutPage = lazy(() => import("./routes/reports/index").then((m) => ({ default: m.ReportsLayout })));
const ReportOverviewPage = lazy(() => import("./routes/reports/overview").then((m) => ({ default: m.OverviewPage })));
const ReportVolumePage = lazy(() => import("./routes/reports/volume").then((m) => ({ default: m.VolumePage })));
const ReportResponseTimesPage = lazy(() => import("./routes/reports/response-times").then((m) => ({ default: m.ResponseTimesPage })));
const ReportTimeInStatusPage = lazy(() => import("./routes/reports/time-in-status").then((m) => ({ default: m.TimeInStatusPage })));
const ReportBacklogPage = lazy(() => import("./routes/reports/backlog").then((m) => ({ default: m.BacklogPage })));
const ReportCsatPage = lazy(() => import("./routes/reports/csat").then((m) => ({ default: m.CsatPage })));
const ReportAgentsPage = lazy(() => import("./routes/reports/agents").then((m) => ({ default: m.AgentsPage })));
const ReportSchedulesPage = lazy(() => import("./routes/reports/schedules").then((m) => ({ default: m.SchedulesPage })));

// Public/login routes do not download the staff workspace and ticket editor.
const loadShell = () => import("./routes/shell");
const loadInbox = () => import("./routes/inbox");
const loadTicket = () => import("./routes/ticket");
const AppShell = lazy(() => loadShell().then((m) => ({ default: m.AppShell })));
const InboxPage = lazy(() => loadInbox().then((m) => ({ default: m.InboxPage })));
const TicketPage = lazy(() => loadTicket().then((m) => ({ default: m.TicketPage })));
const NewTicketPage = lazy(() => import("./routes/new-ticket").then((m) => ({ default: m.NewTicketPage })));
const NotificationsPage = lazy(() => import("./routes/notifications").then((m) => ({ default: m.NotificationsPage })));
const ProfilePage = lazy(() => import("./routes/profile").then((m) => ({ default: m.ProfilePage })));

/** admin/board/embed group — code-split แยก chunk (T-037 R4/R6/R7) */
const AdminHome = lazy(() => import("./routes/admin").then((m) => ({ default: m.AdminHome })));
const AdminServicesPage = lazy(() => import("./routes/admin/services").then((m) => ({ default: m.AdminServicesPage })));
const AdminMailboxesPage = lazy(() => import("./routes/admin/mailboxes").then((m) => ({ default: m.AdminMailboxesPage })));
const AdminTeamsPage = lazy(() => import("./routes/admin/teams").then((m) => ({ default: m.AdminTeamsPage })));
const AdminDepartmentsPage = lazy(() => import("./routes/admin/departments").then((m) => ({ default: m.AdminDepartmentsPage })));
const AdminUsersPage = lazy(() => import("./routes/admin/users").then((m) => ({ default: m.AdminUsersPage })));
const AdminApiClientsPage = lazy(() => import("./routes/admin/api-clients").then((m) => ({ default: m.AdminApiClientsPage })));
const AdminStatusSetsPage = lazy(() => import("./routes/admin/status-sets").then((m) => ({ default: m.AdminStatusSetsPage })));
const AdminPrioritySchemesPage = lazy(() => import("./routes/admin/priority-schemes").then((m) => ({ default: m.AdminPrioritySchemesPage })));
const AdminAutomationPage = lazy(() => import("./routes/admin/automation").then((m) => ({ default: m.AdminAutomationPage })));
const AdminBusinessHoursPage = lazy(() => import("./routes/admin/business-hours").then((m) => ({ default: m.AdminBusinessHoursPage })));
const AdminTemplatesPage = lazy(() => import("./routes/admin/templates").then((m) => ({ default: m.AdminTemplatesPage })));
const AdminSettingsPage = lazy(() => import("./routes/admin/settings").then((m) => ({ default: m.AdminSettingsPage })));
const AdminAuditPage = lazy(() => import("./routes/admin/audit").then((m) => ({ default: m.AdminAuditPage })));
const AdminWebhooksPage = lazy(() => import("./routes/admin/webhooks").then((m) => ({ default: m.AdminWebhooksPage })));
const AdminFormsPage = lazy(() => import("./routes/admin/forms").then((m) => ({ default: m.AdminFormsPage })));
const AdminFieldDefinitionsPage = lazy(() => import("./routes/admin/field-definitions").then((m) => ({ default: m.AdminFieldDefinitionsPage })));
const AdminTagsPage = lazy(() => import("./routes/admin/tags").then((m) => ({ default: m.AdminTagsPage })));
const AdminHolidayCalendarsPage = lazy(() => import("./routes/admin/holiday-calendars").then((m) => ({ default: m.AdminHolidayCalendarsPage })));
// [T-055] W4 admin pages — KB / CSAT / AI / PDPA / flags / health / context providers
const AdminKbPage = lazy(() => import("./routes/admin/kb").then((m) => ({ default: m.AdminKbPage })));
const AdminCsatPage = lazy(() => import("./routes/admin/csat").then((m) => ({ default: m.AdminCsatPage })));
const AdminAiPage = lazy(() => import("./routes/admin/ai").then((m) => ({ default: m.AdminAiPage })));
const AdminPdpaPage = lazy(() => import("./routes/admin/pdpa").then((m) => ({ default: m.AdminPdpaPage })));
const AdminFeatureFlagsPage = lazy(() => import("./routes/admin/feature-flags").then((m) => ({ default: m.AdminFeatureFlagsPage })));
const AdminHealthPage = lazy(() => import("./routes/admin/health").then((m) => ({ default: m.AdminHealthPage })));
const AdminContextProvidersPage = lazy(() => import("./routes/admin/context-providers").then((m) => ({ default: m.AdminContextProvidersPage })));
// [UX-D3 · UX-47] mail admin console — ต่อ /api/v1/mail-admin/* ที่มีอยู่ (M4-01)
const AdminMailPage = lazy(() => import("./routes/admin/mail").then((m) => ({ default: m.MailAdminPage })));
const BoardPage = lazy(() => import("./routes/board").then((m) => ({ default: m.BoardPage })));
// [T-055] W4 staff pages — triage inbox / calendar
const TriagePage = lazy(() => import("./routes/triage").then((m) => ({ default: m.TriagePage })));
const CalendarPage = lazy(() => import("./routes/calendar").then((m) => ({ default: m.CalendarPage })));
// [TM-03] งานภายใน — หน้ารวม (งานของฉัน + โปรเจกต์) + บอร์ด/รายการของโปรเจกต์ (drawer เปิดในหน้าเดียวกัน)
const TasksPage = lazy(() => import("./routes/tasks").then((m) => ({ default: m.TasksPage })));
const TaskProjectPage = lazy(() => import("./routes/tasks-board").then((m) => ({ default: m.TaskProjectPage })));
// [UX-C1 · UX-10] พื้นที่ author ของ KB — /app/kb (shell staff ปกติ แชร์ core กับ admin KB)
const KnowledgePage = lazy(() => import("./routes/knowledge").then((m) => ({ default: m.KnowledgePage })));
// [M4-09] webmail (1) — อ่าน/ค้นหา/labels/quota · code-split ชุดเมล (validator อยู่ที่ queries-mail กันดึงชั้งหลัก)
const MailPage = lazy(() => import("./routes/mail").then((m) => ({ default: m.MailPage })));
const SubmitFormPage = lazy(() => import("./routes/submit").then((m) => ({ default: m.SubmitFormPage })));
const EmbedFormPage = lazy(() => import("./routes/embed").then((m) => ({ default: m.EmbedFormPage })));
// [M4-10] webmail (2) — compose/settings/quarantine (code-split ชุดเมลเดียวกับ M4-09)
const MailComposePage = lazy(() => import("./routes/mail/compose").then((m) => ({ default: m.ComposePage })));
const MailSettingsPage = lazy(() => import("./routes/mail/settings").then((m) => ({ default: m.SettingsPage })));
const MailQuarantinePage = lazy(() => import("./routes/mail/quarantine").then((m) => ({ default: m.QuarantinePage })));

/** portal group (customer/guest) — code-split แยก chunk จาก /app */
const MyTicketsPage = lazy(() => import("./routes/portal").then((m) => ({ default: m.MyTicketsPage })));
const MyTicketPage = lazy(() => import("./routes/portal").then((m) => ({ default: m.MyTicketPage })));
const GuestPage = lazy(() => import("./routes/portal").then((m) => ({ default: m.GuestPage })));
const TrackPage = lazy(() => import("./routes/portal").then((m) => ({ default: m.TrackPage })));
const HomePage = lazy(() => import("./routes/home").then((m) => ({ default: m.HomePage }))); // [T-056]
const KbListPage = lazy(() => import("./routes/kb").then((m) => ({ default: m.KbListPage }))); // [T-056]
const KbArticlePage = lazy(() => import("./routes/kb").then((m) => ({ default: m.KbArticlePage }))); // [T-056]
const CsatRatePage = lazy(() => import("./routes/csat").then((m) => ({ default: m.CsatRatePage }))); // [T-056]
const CsatThanksPage = lazy(() => import("./routes/csat").then((m) => ({ default: m.CsatThanksPage }))); // [T-056]

function Loading() {
  return <PageLoading />;
}

export type RouterContext = { queryClient: QueryClient };

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  notFoundComponent: () => <p className="p-6 text-muted">404</p>,
});

// [T-056] หน้าแรก = portal home (staff ที่ login แล้วยังเข้า /app ได้ตามเดิม)
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => (
    // [T-056]
    <Suspense fallback={<PortalLoading />}>
      {/* [T-056] */}
      <HomePage />
    </Suspense>
  ),
});

// [T-056] KB portal — รายการ + บทความ
const kbRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/kb",
  validateSearch: (s: Record<string, unknown>): { q?: string; category?: string } => ({
    // [T-056]
    q: typeof s.q === "string" ? s.q : undefined, // [T-056]
    category: typeof s.category === "string" ? s.category : undefined, // [T-056]
  }),
  component: function KbListRouteComponent() {
    // [T-056]
    const { q, category } = kbRoute.useSearch(); // [T-056]
    return (
      // [T-056]
      <Suspense fallback={<Loading />}>
        {/* [T-056] */}
        <KbListPage q={q} category={category} />
      </Suspense>
    );
  },
});
const kbArticleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/kb/$slug",
  validateSearch: (s: Record<string, unknown>): { lang?: string } => ({ lang: typeof s.lang === "string" ? s.lang : undefined }), // [T-056]
  component: function KbArticleRouteComponent() {
    // [T-056]
    const { slug } = kbArticleRoute.useParams(); // [T-056]
    const { lang } = kbArticleRoute.useSearch(); // [T-056]
    return (
      // [T-056]
      <Suspense fallback={<Loading />}>
        {/* [T-056] */}
        <KbArticlePage slug={slug} lang={lang} />
      </Suspense>
    );
  },
});

// [T-056] CSAT one-click จากอีเมล — worker redirect มาที่ /csat/rate (ไม่มี score) และ /csat/thanks (หลังบันทึก)
const csatRateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/csat/rate",
  validateSearch: (s: Record<string, unknown>): { token: string; scale?: string } => ({
    // [T-056]
    token: typeof s.token === "string" ? s.token : "", // [T-056]
    scale: typeof s.scale === "string" ? s.scale : undefined, // [T-056]
  }),
  component: function CsatRateRouteComponent() {
    // [T-056]
    const { token, scale } = csatRateRoute.useSearch(); // [T-056]
    return (
      // [T-056]
      <Suspense fallback={<Loading />}>
        {/* [T-056] */}
        <CsatRatePage token={token} scale={scale} />
      </Suspense>
    );
  },
});
const csatThanksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/csat/thanks",
  validateSearch: (s: Record<string, unknown>): { token: string } => ({ token: typeof s.token === "string" ? s.token : "" }), // [T-056]
  component: function CsatThanksRouteComponent() {
    // [T-056]
    const { token } = csatThanksRoute.useSearch(); // [T-056]
    return (
      // [T-056]
      <Suspense fallback={<Loading />}>
        {/* [T-056] */}
        <CsatThanksPage token={token} />
      </Suspense>
    );
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (s: Record<string, unknown>): { next?: string } => (typeof s.next === "string" ? { next: s.next } : {}),
  component: function Login() {
    const { next } = loginRoute.useSearch();
    return <LoginPage next={next} />;
  },
});

const loggedOutRoute = createRoute({ getParentRoute: () => rootRoute, path: "/logged-out", component: LoggedOutPage });

/** /app: โหลด me ก่อน (401 → api.ts พาไป /login); needs_profile → บังคับไป /app/profile */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
  pendingComponent: WorkspaceLoading,
  beforeLoad: async ({ context, location }) => {
    void loadShell().catch(() => undefined);
    const me = await context.queryClient.ensureQueryData(meQuery);
    // [Lead] หลังบ้านเป็นของทีมงานเท่านั้น — ลูกค้า/guest ที่หลุดมาให้กลับไปหน้าของตัวเอง
    if (!me.is_staff) throw redirect({ to: "/my" });
    if (me.user.needs_profile && !location.pathname.startsWith("/app/profile")) {
      throw redirect({ to: "/app/profile" });
    }
    return { me };
  },
  component: () => <Suspense fallback={<WorkspaceLoading />}><AppShell /></Suspense>,
});

const appIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/app/inbox", search: { view: "mine" } });
  },
});

const inboxRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inbox",
  validateSearch: validateInboxSearch,
  loaderDeps: ({ search }) => search,
  loader: async ({ context, deps, preload }) => {
    const qc = context.queryClient;
    void loadInbox().catch(() => undefined);
    if (preload) return;
    // Start independent data before mounting the page; query keys deduplicate observers.
    void qc.prefetchQuery(ticketCountsQuery(deps.team_id));
    void qc.prefetchQuery(workflowStatusSetsQuery);
    if (deps.view_id) void qc.prefetchQuery(viewsQuery);
    const { apiQ } = toApiQuery(deps.q ?? "");
    await qc.prefetchInfiniteQuery(ticketListQuery(deps.view ?? (deps.view_id ? undefined : "mine"), deps.team_id, {
      q: apiQ, sort: deps.sort, view_id: deps.view_id, status_type: deps.status_type,
    }));
  },
  component: function Inbox() {
    const search = inboxRoute.useSearch();
    return <Suspense fallback={<Loading />}><InboxPage search={search} /></Suspense>;
  },
});

const ticketRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/t/$id",
  pendingComponent: () => <PageLoading variant="ticket" />,
  loader: async ({ context, params, preload }) => {
    const qc = context.queryClient;
    void loadTicket().catch(() => undefined);
    // A detail GET can write an admin-view audit. Hover should warm code, not access the ticket.
    if (preload) return;
    void qc.prefetchQuery(ticketLinksQuery(params.id));
    void qc.prefetchQuery(watchersQuery(params.id));
    void qc.prefetchQuery(composeContextQuery(params.id));
    await qc.prefetchQuery(ticketQuery(params.id));
  },
  component: function TicketRouteComponent() {
    const { id } = ticketRoute.useParams();
    return <Suspense fallback={<PageLoading variant="ticket" />}><TicketPage key={id} id={id} /></Suspense>;
  },
});

const newTicketRoute = createRoute({ getParentRoute: () => appRoute, path: "/new", component: () => <Suspense fallback={<Loading />}><NewTicketPage /></Suspense> });
const profileRoute = createRoute({ getParentRoute: () => appRoute, path: "/profile", component: () => <Suspense fallback={<Loading />}><ProfilePage /></Suspense> });
const notificationsRoute = createRoute({ getParentRoute: () => appRoute, path: "/notifications", component: () => <Suspense fallback={<Loading />}><NotificationsPage /></Suspense> });

const boardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/board",
  validateSearch: (s: Record<string, unknown>): { view?: string } => (typeof s.view === "string" ? { view: s.view } : {}),
  component: function BoardRouteComponent() {
    const { view } = boardRoute.useSearch();
    return (
      <Suspense fallback={<PageLoading variant="board" />}>
        <BoardPage viewId={view} />
      </Suspense>
    );
  },
});

// [TM-03] งานภายใน — /app/tasks (งานของฉัน + โปรเจกต์) และ /app/tasks/:projectId (บอร์ด/รายการ + drawer)
const tasksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/tasks",
  component: () => (
    <Suspense fallback={<Loading />}>
      <TasksPage />
    </Suspense>
  ),
});
const tasksProjectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/tasks/$projectId",
  validateSearch: validateTasksSearch,
  component: function TaskProjectRouteComponent() {
    const { projectId } = tasksProjectRoute.useParams();
    const search = tasksProjectRoute.useSearch();
    const navigate = useNavigate();
    const setSearch = (patch: Partial<TasksSearch>) => {
      void navigate({ to: "/app/tasks/$projectId", params: { projectId }, search: { ...search, ...patch } });
    };
    return (
      <Suspense fallback={<PageLoading variant={search.view === "board" ? "board" : "list"} />}>
        <TaskProjectPage projectId={projectId} search={search} setSearch={setSearch} />
      </Suspense>
    );
  },
});

// [T-055 R3] triage inbox
const triageRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/triage",
  validateSearch: (s: Record<string, unknown>): { team_id?: string } => (typeof s.team_id === "string" ? { team_id: s.team_id } : {}),
  component: function TriageRouteComponent() {
    return (
      <Suspense fallback={<Loading />}>
        <TriagePage />
      </Suspense>
    );
  },
});

// [T-055 R6] calendar — month/week จาก due_at ของ saved view (layout=calendar)
const calendarRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/calendar",
  validateSearch: (s: Record<string, unknown>): { view?: string; mode?: "month" | "week" } => ({
    ...(typeof s.view === "string" ? { view: s.view } : {}),
    ...(s.mode === "week" ? { mode: "week" as const } : s.mode === "month" ? { mode: "month" as const } : {}),
  }),
  component: function CalendarRouteComponent() {
    const { view, mode } = calendarRoute.useSearch();
    return (
      <Suspense fallback={<PageLoading variant="board" />}>
        <CalendarPage viewId={view} mode={mode ?? "month"} />
      </Suspense>
    );
  },
});

// [UX-C1 · UX-10] พื้นที่ author ของ KB (/app/kb) — staff shell ปกติ แชร์ core กับ admin KB
const knowledgeKbRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/kb",
  component: () => (
    <Suspense fallback={<Loading />}>
      <KnowledgePage />
    </Suspense>
  ),
});

// [M4-09] webmail — flag mail.webmail gate อยู่ในหน้า (ปิด = 404 + Sidebar ซ่อนเมนู)
const mailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/mail",
  validateSearch: (s: Record<string, unknown>) => validateMailSearch(s),
  component: function MailRouteComponent() {
    const search = mailRoute.useSearch();
    return (
      <Suspense fallback={<Loading />}>
        <MailPage search={search} />
      </Suspense>
    );
  },
});
// [M4-09] ทางลัด /mail → /app/mail (e2e/ใบงานอ้าง URL สั้น)
const mailShortcutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mail",
  beforeLoad: () => {
    throw redirect({ to: "/app/mail" });
  },
});

// [M4-10] webmail (2) — compose/settings/quarantine · search whitelist ตาม ComposeSearch (M4-02G: ไม่มีผลกับ route นี้)
// [A3] + account_id ของกล่องที่ใช้ (D-042) — ทุกหน้าเมลรับ/คงไว้เหมือนกัน
function m410Search(s: Record<string, unknown>): { draft?: string; reply_message_id?: string; reply_type?: "reply" | "reply_all" | "forward"; to?: string; subject?: string; account_id?: string } {
  const replyType = s.reply_type === "reply_all" || s.reply_type === "forward" ? s.reply_type : s.reply_type === "reply" ? "reply" : undefined;
  return {
    ...(typeof s.draft === "string" && s.draft ? { draft: s.draft } : {}),
    ...(typeof s.reply_message_id === "string" && s.reply_message_id ? { reply_message_id: s.reply_message_id } : {}),
    ...(replyType ? { reply_type: replyType } : {}),
    ...(typeof s.to === "string" && s.to ? { to: s.to } : {}),
    ...(typeof s.subject === "string" && s.subject ? { subject: s.subject } : {}),
    ...(typeof s.account_id === "string" && s.account_id ? { account_id: s.account_id } : {}),
  };
}
// [A3] settings/quarantine — whitelist account_id ให้ URL คงกล่องที่เลือกไว้ได้
function mailAccountSearch(s: Record<string, unknown>): { account_id?: string } {
  return typeof s.account_id === "string" && s.account_id ? { account_id: s.account_id } : {};
}
const mailComposeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/mail/compose",
  validateSearch: m410Search,
  component: () => (
    <Suspense fallback={<Loading />}>
      <MailComposePage />
    </Suspense>
  ),
});
const mailSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/mail/settings",
  validateSearch: mailAccountSearch,
  component: () => (
    <Suspense fallback={<Loading />}>
      <MailSettingsPage />
    </Suspense>
  ),
});
const mailQuarantineRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/mail/quarantine",
  validateSearch: mailAccountSearch,
  component: () => (
    <Suspense fallback={<Loading />}>
      <MailQuarantinePage />
    </Suspense>
  ),
});

// [T-068 R1/R2] /app/reports — ค่ากรองทั้งหมดอยู่ใน URL search params · แต่ละแท็บเป็น route ย่อยของตัวเอง (กด back ได้)
const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports",
  validateSearch: (s: Record<string, unknown>): ReportsSearch => normalizeReportsSearch(s),
  component: function ReportsRouteComponent() {
    const { me } = reportsRoute.useRouteContext();
    return (
      <Suspense fallback={<Loading />}>
        <ReportsLayoutPage me={me} />
      </Suspense>
    );
  },
});
function reportTabPage(Comp: React.ComponentType<{ search: ReportsSearch }>) {
  return function ReportTabComponent() {
    const search = reportsRoute.useSearch();
    return (
      <Suspense fallback={<Loading />}>
        <Comp search={search} />
      </Suspense>
    );
  };
}
const reportsIndexRoute = createRoute({ getParentRoute: () => reportsRoute, path: "/", component: reportTabPage(ReportOverviewPage) });
const reportsVolumeRoute = createRoute({ getParentRoute: () => reportsRoute, path: "/volume", component: reportTabPage(ReportVolumePage) });
const reportsResponseTimesRoute = createRoute({ getParentRoute: () => reportsRoute, path: "/response-times", component: reportTabPage(ReportResponseTimesPage) });
const reportsTimeInStatusRoute = createRoute({ getParentRoute: () => reportsRoute, path: "/time-in-status", component: reportTabPage(ReportTimeInStatusPage) });
const reportsBacklogRoute = createRoute({ getParentRoute: () => reportsRoute, path: "/backlog", component: reportTabPage(ReportBacklogPage) });
const reportsCsatRoute = createRoute({ getParentRoute: () => reportsRoute, path: "/csat", component: reportTabPage(ReportCsatPage) });
const reportsAgentsRoute = createRoute({ getParentRoute: () => reportsRoute, path: "/agents", component: reportTabPage(ReportAgentsPage) });
const reportsSchedulesRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: "/schedules",
  component: function ReportsSchedulesRouteComponent() {
    const { me } = reportsRoute.useRouteContext();
    return (
      <Suspense fallback={<Loading />}>
        <ReportSchedulesPage me={me} />
      </Suspense>
    );
  },
});

/** /app/admin/* — layout ตรวจ is_admin เอง (AdminHome) + Suspense ต่อหน้าย่อย (code-split) */
const adminRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/admin",
  component: () => (
    <Suspense fallback={<Loading />}>
      <AdminHome />
    </Suspense>
  ),
});
function adminChild<TPath extends string>(path: TPath, Comp: React.ComponentType) {
  return createRoute({
    getParentRoute: () => adminRoute,
    path,
    component: () => (
      <Suspense fallback={<Loading />}>
        <Comp />
      </Suspense>
    ),
  });
}
const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/app/admin/services" });
  },
});
const adminServicesRoute = adminChild("/services", AdminServicesPage);
const adminMailboxesRoute = adminChild("/mailboxes", AdminMailboxesPage);
const adminTeamsRoute = adminChild("/teams", AdminTeamsPage);
const adminDepartmentsRoute = adminChild("/departments", AdminDepartmentsPage);
const adminUsersRoute = adminChild("/users", AdminUsersPage);
const adminApiClientsRoute = adminChild("/api-clients", AdminApiClientsPage);
const adminStatusSetsRoute = adminChild("/status-sets", AdminStatusSetsPage);
const adminPrioritySchemesRoute = adminChild("/priority-schemes", AdminPrioritySchemesPage);
const adminAutomationRoute = adminChild("/automation", AdminAutomationPage);
const adminBusinessHoursRoute = adminChild("/business-hours", AdminBusinessHoursPage);
const adminTemplatesRoute = adminChild("/templates", AdminTemplatesPage);
const adminSettingsRoute = adminChild("/settings", AdminSettingsPage);
const adminAuditRoute = adminChild("/audit", AdminAuditPage);
const adminWebhooksRoute = adminChild("/webhooks", AdminWebhooksPage);
const adminFormsRoute = adminChild("/forms", AdminFormsPage);
const adminFieldDefinitionsRoute = adminChild("/field-definitions", AdminFieldDefinitionsPage);
const adminTagsRoute = adminChild("/tags", AdminTagsPage);
const adminHolidayCalendarsRoute = adminChild("/holiday-calendars", AdminHolidayCalendarsPage);
// [T-055] W4 admin routes
const adminKbRoute = adminChild("/kb", AdminKbPage);
const adminCsatRoute = adminChild("/csat", AdminCsatPage);
const adminAiRoute = adminChild("/ai", AdminAiPage);
const adminPdpaRoute = adminChild("/pdpa", AdminPdpaPage);
const adminFeatureFlagsRoute = adminChild("/feature-flags", AdminFeatureFlagsPage);
const adminHealthRoute = adminChild("/health", AdminHealthPage);
const adminContextProvidersRoute = adminChild("/context-providers", AdminContextProvidersPage);
const adminMailRoute = adminChild("/mail", AdminMailPage); // [UX-D3 · UX-47]

const myRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/my",
  component: () => (
    <Suspense fallback={<Loading />}>
      <MyTicketsPage />
    </Suspense>
  ),
});
const myTicketRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/my/t/$id",
  component: function MyTicketRouteComponent() {
    const { id } = myTicketRoute.useParams();
    return (
      <Suspense fallback={<Loading />}>
        <MyTicketPage id={id} />
      </Suspense>
    );
  },
});
const guestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/g/$token",
  component: function GuestRouteComponent() {
    const { token } = guestRoute.useParams();
    return (
      <Suspense fallback={<Loading />}>
        <GuestPage token={token} />
      </Suspense>
    );
  },
});
const trackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/track",
  component: () => (
    <Suspense fallback={<Loading />}>
      <TrackPage />
    </Suspense>
  ),
});

const submitRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/submit/$slug",
  component: function SubmitRouteComponent() {
    const { slug } = submitRoute.useParams();
    return (
      <Suspense fallback={<Loading />}>
        <SubmitFormPage slug={slug} />
      </Suspense>
    );
  },
});

const embedFormRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/embed/form/$slug",
  component: function EmbedFormRouteComponent() {
    const { slug } = embedFormRoute.useParams();
    return (
      <Suspense fallback={<Loading />}>
        <EmbedFormPage slug={slug} />
      </Suspense>
    );
  },
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  loggedOutRoute,
  myRoute,
  myTicketRoute,
  guestRoute,
  trackRoute,
  submitRoute,
  embedFormRoute,
  kbRoute, // [T-056]
  kbArticleRoute, // [T-056]
  csatRateRoute, // [T-056]
  csatThanksRoute, // [T-056]
  mailShortcutRoute, // [M4-09]
  appRoute.addChildren([
    appIndexRoute,
    inboxRoute,
    ticketRoute,
    newTicketRoute,
    profileRoute,
    notificationsRoute,
    boardRoute,
    tasksRoute, // [TM-03]
    tasksProjectRoute, // [TM-03]
    triageRoute, // [T-055]
    calendarRoute, // [T-055]
    knowledgeKbRoute, // [UX-C1 · UX-10]
    mailRoute, // [M4-09]
    // [M4-10] webmail (2)
    mailComposeRoute,
    mailSettingsRoute,
    mailQuarantineRoute,
    reportsRoute.addChildren([
      // [T-068]
      reportsIndexRoute,
      reportsVolumeRoute,
      reportsResponseTimesRoute,
      reportsTimeInStatusRoute,
      reportsBacklogRoute,
      reportsCsatRoute,
      reportsAgentsRoute,
      reportsSchedulesRoute,
    ]),
    adminRoute.addChildren([
      adminIndexRoute,
      adminServicesRoute,
      adminMailboxesRoute,
      adminTeamsRoute,
      adminDepartmentsRoute,
      adminUsersRoute,
      adminApiClientsRoute,
      adminStatusSetsRoute,
      adminPrioritySchemesRoute,
      adminAutomationRoute,
      adminBusinessHoursRoute,
      adminTemplatesRoute,
      adminSettingsRoute,
      adminAuditRoute,
      adminWebhooksRoute,
      adminFormsRoute,
      adminFieldDefinitionsRoute,
      adminTagsRoute,
      adminHolidayCalendarsRoute,
      adminKbRoute, // [T-055]
      adminCsatRoute, // [T-055]
      adminAiRoute, // [T-055]
      adminPdpaRoute, // [T-055]
      adminFeatureFlagsRoute, // [T-055]
      adminHealthRoute, // [T-055]
      adminContextProvidersRoute, // [T-055]
      adminMailRoute, // [UX-D3 · UX-47]
    ]),
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPendingComponent: Loading,
    // Keep the current route for quick reads rather than flashing a placeholder.
    defaultPendingMs: 200,
    defaultPendingMinMs: 0,
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
